/**
 * cameraShake.ts
 * Camera-shake driver for the World 2 space scene.
 *
 * Two responsibilities:
 *   1. {@link triggerSpaceShake} — start a new shake event with a peak
 *      magnitude (px) and duration (s). Replaces any in-flight shake
 *      whose peak is smaller; never weakens a louder shake already in
 *      progress (so a missile detonation can't shorten a ship-hit
 *      shake that just started).
 *   2. {@link tickCameraShake} — advance the active shake by one fixed
 *      step, decaying its magnitude linearly toward zero and writing a
 *      fresh `(shakeX, shakeY)` random offset.
 *
 * RNG is module-local and overridable for tests via
 * {@link setShakeRng} / {@link resetShakeRng}, mirroring the pattern
 * used in `spaceEnemySystem` so a deterministic seed gives a
 * deterministic shake.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module cameraShake
 */

import { FIXED_DT } from '../../constants/gameConstants';
import { spaceState } from './spaceState';

/** Default RNG — overridable for deterministic tests. */
let rng: () => number = Math.random;

/**
 * Override the shake-driver RNG (test hook). Pair with
 * {@link resetShakeRng} in an `afterEach`.
 *
 * @param fn - Replacement RNG returning `[0, 1)`.
 */
export function setShakeRng(fn: () => number): void {
  rng = fn;
}

/** Restore the default `Math.random` RNG. */
export function resetShakeRng(): void {
  rng = Math.random;
}

/**
 * Start a new camera-shake event. Larger peaks override smaller
 * in-flight events; smaller peaks during a larger event are ignored so
 * a faint hit can't drown out a heavier one mid-decay.
 *
 * Duration is clamped to a minimum of one fixed tick (`FIXED_DT`) so a
 * caller can't arm a zero-length event. {@link tickCameraShake}
 * guarantees the offset renders for at least one frame regardless.
 *
 * @param peakMagnitudePx - Maximum offset radius in logical px (≥ 0).
 * @param durationS - Seconds over which the shake decays linearly to 0 (> 0).
 */
export function triggerSpaceShake(
  peakMagnitudePx: number,
  durationS: number,
): void {
  if (peakMagnitudePx <= 0 || durationS <= 0) return;
  // DECISION: A louder shake in-flight wins. Otherwise a missile that
  // detonates one tick after a ship hit would shorten the heavier
  // ship-hit shake and the player loses the original feedback.
  if (peakMagnitudePx < spaceState.shakePeakMagnitude && spaceState.shakeTimer > 0) {
    return;
  }
  // DECISION: Clamp to at least one fixed tick so a caller can't arm a
  // zero/sub-tick event. triggerSpaceShake runs during damage resolution and
  // tickCameraShake runs later the same tick (spaceStep.ts section 9f); the
  // tick writes its offset BEFORE decaying the timer so even a one-tick shake
  // renders a non-zero offset for one frame (see tickCameraShake).
  const clampedDuration = Math.max(FIXED_DT, durationS);
  spaceState.shakePeakMagnitude = peakMagnitudePx;
  spaceState.shakeDurationPeak = clampedDuration;
  spaceState.shakeTimer = clampedDuration;
  spaceState.shakeMagnitude = peakMagnitudePx;
}

/**
 * Advance the active shake by one fixed step. Decays
 * `shakeMagnitude` linearly toward zero and writes a fresh random
 * offset into `(shakeX, shakeY)`. No-op when no shake is active.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function tickCameraShake(dt: number): void {
  if (spaceState.shakeTimer <= 0) {
    if (spaceState.shakeX !== 0 || spaceState.shakeY !== 0) {
      spaceState.shakeX = 0;
      spaceState.shakeY = 0;
    }
    return;
  }
  // DECISION: Write this tick's offset from the magnitude at the START of the
  // tick, THEN decay the timer. Writing before the decrement guarantees every
  // armed shake renders at least one non-zero offset frame — even a one-tick
  // shake (durationS === FIXED_DT), where decrementing first would land
  // shakeTimer on 0 and zero the offset before the renderer ever read it.
  const ratio = spaceState.shakeTimer / spaceState.shakeDurationPeak;
  spaceState.shakeMagnitude = spaceState.shakePeakMagnitude * ratio;
  // Centred random offset in [-mag, +mag] on each axis.
  spaceState.shakeX = (rng() - 0.5) * 2 * spaceState.shakeMagnitude;
  spaceState.shakeY = (rng() - 0.5) * 2 * spaceState.shakeMagnitude;

  spaceState.shakeTimer = Math.max(0, spaceState.shakeTimer - dt);
  if (spaceState.shakeTimer <= 0) {
    // Timer exhausted: this was the final active frame. Clear the bookkeeping
    // so the next tick early-returns; the offset written above still renders
    // for the current frame before the next tick zeroes it.
    spaceState.shakeMagnitude = 0;
    spaceState.shakePeakMagnitude = 0;
    spaceState.shakeDurationPeak = 0;
  }
}

/**
 * Force the active shake to zero. Called from scene teardown so a
 * shake never leaks across sessions.
 */
export function resetCameraShake(): void {
  spaceState.shakeX = 0;
  spaceState.shakeY = 0;
  spaceState.shakeMagnitude = 0;
  spaceState.shakeTimer = 0;
  spaceState.shakeDurationPeak = 0;
  spaceState.shakePeakMagnitude = 0;
}
